import { Plus, Target, Trash, CheckCircle, WarningCircle, Clock, ChartLineUp } from "@phosphor-icons/react";

const METRIC_LABEL = {
  citations: "Citations",
  h_index: "h-index",
  i10_index: "i10-index",
};

const STATUS_PRESET = {
  achieved: {
    label: "Achieved",
    badge: "bg-teal text-white border-teal",
    bar: "bg-teal",
    icon: CheckCircle,
    accent: "text-teal",
  },
  on_track: {
    label: "On track",
    badge: "bg-saffron/20 text-saffron-hover border-saffron",
    bar: "bg-saffron",
    icon: ChartLineUp,
    accent: "text-saffron-hover",
  },
  behind: {
    label: "Behind pace",
    badge: "bg-terracotta/15 text-terracotta border-terracotta",
    bar: "bg-terracotta",
    icon: Clock,
    accent: "text-terracotta",
  },
  overdue: {
    label: "Overdue",
    badge: "bg-ink text-paper border-ink",
    bar: "bg-ink",
    icon: WarningCircle,
    accent: "text-ink",
  },
};

function formatDeadline(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export const Goals = ({ goals = [], onAdd, onDelete }) => {
  return (
    <div data-testid="goals-section">
      <div className="flex items-center justify-end mb-4 no-print">
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-2 bg-saffron text-ink px-4 py-2 text-xs font-mono uppercase tracking-wider hover:bg-saffron-hover hover:text-paper transition-colors border border-saffron"
          data-testid="add-goal-btn"
        >
          <Plus size={14} weight="bold" />
          Add milestone
        </button>
      </div>

      {goals.length === 0 ? (
        <div
          className="editorial-card p-10 text-center"
          data-testid="goals-empty"
        >
          <Target size={32} weight="duotone" className="text-saffron-hover mx-auto mb-3" />
          <div className="font-display text-2xl mb-2">No milestones set yet</div>
          <p className="text-sm text-ink-mute max-w-md mx-auto">
            Set a target citation count, h-index, or i10-index to track against a deadline.
            We&apos;ll show you the pace and whether you&apos;re on track.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          {goals.map((g) => {
            const preset = STATUS_PRESET[g.status] || STATUS_PRESET.on_track;
            const Icon = preset.icon;
            const pct = Math.min(g.progress_pct, 100);
            return (
              <div
                key={g.goal.id}
                className="editorial-card p-6 lg:p-7 relative"
                data-testid={`goal-card-${g.goal.id}`}
              >
                <div className={`absolute top-0 left-0 h-1 w-full ${preset.bar}`} />
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <div className={`inline-flex items-center gap-1.5 pill-color ${preset.badge} mb-2`}>
                      <Icon size={12} weight="bold" />
                      {preset.label}
                    </div>
                    <div className="font-display text-2xl tracking-tight leading-tight">
                      {g.goal.label || `Reach ${g.goal.target.toLocaleString()} ${METRIC_LABEL[g.goal.metric].toLowerCase()}`}
                    </div>
                    <div className="text-xs font-mono text-ink-mute mt-1.5 uppercase tracking-wider">
                      Target {g.goal.target.toLocaleString()} {METRIC_LABEL[g.goal.metric]} · by {formatDeadline(g.goal.deadline)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onDelete(g.goal.id)}
                    className="p-1.5 text-ink-mute hover:text-terracotta transition-colors no-print"
                    aria-label="Delete goal"
                    data-testid={`goal-delete-${g.goal.id}`}
                  >
                    <Trash size={16} weight="duotone" />
                  </button>
                </div>

                <div className="space-y-2 mt-4">
                  <div className="flex items-baseline justify-between font-mono text-xs">
                    <span className="text-ink-mute uppercase tracking-wider">Progress</span>
                    <span className={`${preset.accent} font-medium`}>
                      {g.current.toLocaleString()} / {g.goal.target.toLocaleString()}
                      {" "}· {pct.toFixed(1)}%
                    </span>
                  </div>
                  <div className="h-2 bg-paper-alt overflow-hidden border border-paper-edge">
                    <div
                      className={`h-full ${preset.bar} transition-all duration-500`}
                      style={{ width: `${pct}%` }}
                      data-testid={`goal-progress-bar-${g.goal.id}`}
                    />
                  </div>
                  <div className="flex justify-between text-[11px] font-mono text-ink-mute pt-1">
                    <span>
                      {g.days_remaining < 0
                        ? `${Math.abs(g.days_remaining)} days past`
                        : `${g.days_remaining} days remaining`}
                    </span>
                    {g.goal.note && (
                      <span className="italic text-right max-w-[55%] truncate">{g.goal.note}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Goals;
